import React from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import ToolTip from '../ToolTipIcon/ToolTip';

/**
 * Checks if a given URL is external to the current origin.
 * @param {string} url - The URL to check.
 * @returns {boolean} - True if the URL is external, false otherwise.
 */
export const isExternal = url => {
  const target = new URL(url, globalThis.location.origin);

  return target.origin !== globalThis.location.origin;
};

const cssClass = 'protected-link';

/**
 * Normalizes a className string by ensuring the component's CSS class is included
 * and removing duplicates or empty strings.
 * @param {string} className - The original className string.
 * @returns {string} - The normalized className string.
 */
export const normalizeClassName = className => {
  const classNames = new Set(className.split(' ').filter(Boolean));

  classNames.add(cssClass);

  return Array.from(classNames).join(' ');
};

/**
 * Tells if the input url is matching the current location
 * @param {string} currentUrl
 * @param {string} currentLocation
 * @return {boolean}
 */
export const isActive = (currentUrl, currentLocation) => {
  return currentUrl === currentLocation;
};

/**
 * A smart link component that automatically renders the appropriate element
 * based on the destination.
 *
 * Rendering behavior:
 * - External URLs (different origin) render as a native `<a>` element.
 * - Internal URLs render as either a `Link` or `NavLink` from `react-router-dom`.
 * - When `disabled` is `true`, a non-interactive `<span>` is rendered instead,
 *   preventing navigation while allowing disabled styling.
 *
 * @component
 *
 * @param {Object} props
 * @param {boolean} props.allowed Indicates whether the link is enabled or disabled.
 * @param {string} [props.toolText] Tooltip text displayed when the link is disabled.
 * @param {string} props.to Destination URL or route.
 * @param {React.ReactNode} props.children Content displayed inside the link.
 * @param {boolean} [props.nav=false]
 * When true, renders a NavLink instead of a Link. Use this only for
 * application navigation (sidebars, menus, tabs) where active-route styling is required.
 *
 * Use `nav={true}` for:
 * - Main navigation bars
 * - Sidebars
 * - Navigation drawers
 * - Tab navigation
 *
 * Do NOT use `nav` for:
 * - "View Details" links
 * - Buttons that navigate
 * - Links inside cards, tables, or lists
 * - Breadcrumb links
 * - Any navigation where an active state is unnecessary
 * @param {boolean} [props.external=false] When true, forces the link to be treated as external.
 * @param {string} [props.className] CSS classes applied to the rendered element.
 * @param {string} [props.rel] The `rel` attribute for the rendered link, used for external links.
 * @param {string} [props.target] The `target` attribute for the rendered link, used for external links.
 * @param {string} [props.activeClass] sets this css class if the url is the same as the navigation is at
 */
export default function ProtectedLink({
  allowed,
  toolText = 'You do not have permission to access this link',
  to,
  children,
  nav = false,
  external = false,
  className = '',
  rel,
  target,
  activeClass = 'active',
}) {
  const location = useLocation();
  const currentLocation = location.pathname + location.search;
  const cssClasses = normalizeClassName(
    `${className} ${isActive(to, currentLocation) ? activeClass : ''}`,
  );

  if (!allowed) {
    // getCssClasses needs to be called to produce a string
    return (
      <ToolTip toolText={toolText}>
        <span className={`${cssClasses} disabled`} aria-disabled="true">
          {children}
        </span>
      </ToolTip>
    );
  }

  if (external || isExternal(to)) {
    // This protects against reverse tabnabbing if a caller forgets to specify rel.
    const anchorRel = target === '_blank' ? (rel ?? 'noopener noreferrer') : rel;

    // getCssClasses needs to be called to produce a string */
    return (
      <a href={to} className={cssClasses} rel={anchorRel} target={target}>
        {children}
      </a>
    );
  }

  if (nav) {
    // getCssClasses passed by reference to Navlink
    return (
      <NavLink to={to} className={cssClasses}>
        {children}
      </NavLink>
    );
  }

  // getCssClasses needs to be called to produce a string
  return (
    <Link to={to} className={cssClasses}>
      {children}
    </Link>
  );
}

ProtectedLink.propTypes = {
  allowed: PropTypes.bool.isRequired,
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  nav: PropTypes.bool,
  external: PropTypes.bool,
  className: PropTypes.string,
  toolText: PropTypes.string,
  rel: PropTypes.string,
  target: PropTypes.string,
  activeClass: PropTypes.string,
};
